import { clsx } from "clsx";
import type { MaterialSymbol } from "material-symbols";
import type { CSSProperties, ForwardedRef, ReactElement } from "react";
import { type AriaButtonProps, FocusRing, type HoverEvents, mergeProps, useButton, useHover } from "react-aria";

import { useDOMRef } from "../../hooks.ts";
import { useDataAttributes } from "../../hooks/useDataAttributes.ts";
import { forwardRef } from "../../utils/jsx/forwardRef.ts";
import { Icon } from "../Icon/Icon.tsx";
import { VStack } from "../Stack/VStack.tsx";
import style from "./StartupButton.module.css";

export interface StartupButtonProps extends AriaButtonProps, HoverEvents {
  label: string;
  description?: string;
  icon?: ReactElement | MaterialSymbol;
  className?: string;
  style?: CSSProperties;
  intent?: "outlined" | "primary";
}

const StartupButtonRender = (props: StartupButtonProps, ref: ForwardedRef<HTMLButtonElement>) => {
  const triggerRef = useDOMRef<HTMLButtonElement>(ref);

  const { buttonProps } = useButton({ ...props }, triggerRef);
  const { hoverProps } = useHover({ ...props });

  const dataAttributes = useDataAttributes(props);

  return (
    <FocusRing focusRingClass={style.focus}>
      <button
        ref={triggerRef}
        type="button"
        {...dataAttributes}
        {...mergeProps(hoverProps, buttonProps)}
        className={clsx(style.button, props.className, { [style.reverse]: props.intent === "primary" })}
        style={props.style}
        data-testid={`${dataAttributes["data-testid"] ? `${dataAttributes["data-testid"]}__` : ""}startup-button`}
      >
        <div>
          <Icon icon={props.icon} className={style.icon} />
        </div>
        <VStack style={{ alignItems: "flex-start", textAlign: "left" }}>
          <div className={style.label}>{props.label}</div>
          {props.description != undefined && <div className={style.description}>{props.description}</div>}
        </VStack>
      </button>
    </FocusRing>
  );
};

export const StartupButton = forwardRef(StartupButtonRender);
